import React, { useState } from 'react';
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Snackbar,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
  Divider
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import { reportCardAPI } from '../../services/api';

const PAPER_TYPES = ['Theory', 'Practical', 'Coursework', 'Project', 'Oral', 'Written'];
const CATEGORIES = ['Core', 'Elective', 'Extra'];

const SubjectWithPapers = () => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);
  const [subject, setSubject] = useState({
    subject_name: '',
    short_name: '',
    description: '',
    category: 'Core',
    is_active: true
  });
  const [papers, setPapers] = useState([
    { paper_code: '', paper_name: '', paper_number: 1, paper_type: 'Theory', description: '', is_active: true }
  ]);

  const addPaper = () => {
    setPapers([...papers, {
      paper_code: '',
      paper_name: '',
      paper_number: papers.length + 1,
      paper_type: 'Theory',
      description: '',
      is_active: true
    }]);
  };

  const removePaper = (index) => {
    if (papers.length === 1) {
      setSnackbar({ open: true, message: 'At least one paper is required', severity: 'warning' });
      return;
    }
    setPapers(papers.filter((_, i) => i !== index));
  };

  const updatePaper = (index, field, value) => {
    const updated = [...papers];
    updated[index] = { ...updated[index], [field]: value };
    setPapers(updated);
  };

  const validateForm = () => {
    if (!subject.subject_name.trim()) {
      setSnackbar({ open: true, message: 'Subject name is required', severity: 'error' });
      return false;
    }
    if (!subject.short_name.trim()) {
      setSnackbar({ open: true, message: 'Short name is required', severity: 'error' });
      return false;
    }
    for (let i = 0; i < papers.length; i++) {
      if (!papers[i].paper_code.trim()) {
        setSnackbar({ open: true, message: `Paper ${i + 1}: Code is required`, severity: 'error' });
        return false;
      }
      if (!papers[i].paper_name.trim()) {
        setSnackbar({ open: true, message: `Paper ${i + 1}: Name is required`, severity: 'error' });
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!subject.subject_name.trim()) {
      setSnackbar({ open: true, message: 'Subject name is required', severity: 'error' });
      return;
    }
    if (!subject.short_name.trim()) {
      setSnackbar({ open: true, message: 'Short name is required', severity: 'error' });
      return;
    }

    // Check if papers have data
    const hasValidPapers = papers.some(p => p.paper_code.trim() && p.paper_name.trim());

    setSaving(true);
    try {
      // Create subject
      const subjectResponse = await reportCardAPI.storeSubject(subject);
      const createdSubject = subjectResponse.data?.data || subjectResponse.data;

      if (hasValidPapers) {
        // Validate and create only filled papers
        const validPapers = papers.filter(p => p.paper_code.trim() && p.paper_name.trim());
        const paperPromises = validPapers.map(paper =>
          reportCardAPI.storeSubjectPaper({
            ...paper,
            subject_id: createdSubject.id
          })
        );
        await Promise.all(paperPromises);

        setSnackbar({
          open: true,
          message: `Subject "${subject.subject_name}" created with ${validPapers.length} paper(s) successfully!`,
          severity: 'success'
        });
      } else {
        // No papers added - create default paper with subject name
        await reportCardAPI.storeSubjectPaper({
          subject_id: createdSubject.id,
          paper_code: subject.short_name,
          paper_name: subject.subject_name,
          paper_number: 1,
          paper_type: 'Theory',
          description: 'Default paper',
          is_active: true
        });

        setSnackbar({
          open: true,
          message: `Subject "${subject.subject_name}" created with default paper successfully!`,
          severity: 'success'
        });
      }

      // Reset form
      setSubject({
        subject_name: '',
        short_name: '',
        description: '',
        category: 'Core',
        is_active: true
      });
      setPapers([
        { paper_code: '', paper_name: '', paper_number: 1, paper_type: 'Theory', description: '', is_active: true }
      ]);
    } catch (error) {
      console.error('Error creating subject with papers:', error);
      const errorMsg = error?.response?.data?.message || 'Failed to create subject with papers';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>Add Subject with Papers</Typography>
        <Typography variant="body1" color="text.secondary">
          Create a subject and all its papers in one go
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Subject Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Subject Name"
                value={subject.subject_name}
                onChange={(e) => setSubject({ ...subject, subject_name: e.target.value })}
                placeholder="e.g., Mathematics"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Short Name"
                value={subject.short_name}
                onChange={(e) => setSubject({ ...subject, short_name: e.target.value })}
                placeholder="e.g., MATH"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={CATEGORIES}
                value={subject.category || null}
                onChange={(_, value) => setSubject({ ...subject, category: value || '' })}
                renderInput={(params) => <TextField {...params} label="Category" required />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={subject.is_active}
                    onChange={(e) => setSubject({ ...subject, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={subject.description}
                onChange={(e) => setSubject({ ...subject, description: e.target.value })}
                placeholder="Optional description"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Subject Papers (Optional)</Typography>
            <Button startIcon={<AddIcon />} onClick={addPaper} size="small">
              Add Paper
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Leave papers empty to create a default paper with the subject name, or add specific papers below.
          </Typography>

          {papers.map((paper, index) => (
            <Box key={index} mb={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" color="primary">Paper {index + 1}</Typography>
                <IconButton size="small" color="error" onClick={() => removePaper(index)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Paper Code"
                    value={paper.paper_code}
                    onChange={(e) => updatePaper(index, 'paper_code', e.target.value)}
                    placeholder="e.g., MATH-P1"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Paper Name"
                    value={paper.paper_name}
                    onChange={(e) => updatePaper(index, 'paper_name', e.target.value)}
                    placeholder="e.g., Paper 1"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Paper Number"
                    value={paper.paper_number}
                    onChange={(e) => updatePaper(index, 'paper_number', parseInt(e.target.value))}
                    inputProps={{ min: 1 }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={PAPER_TYPES}
                    value={paper.paper_type || null}
                    onChange={(_, value) => updatePaper(index, 'paper_type', value || '')}
                    renderInput={(params) => <TextField {...params} label="Type" required />}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={paper.is_active}
                        onChange={(e) => updatePaper(index, 'is_active', e.target.checked)}
                        size="small"
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={paper.description}
                    onChange={(e) => updatePaper(index, 'description', e.target.value)}
                    placeholder="Optional description"
                  />
                </Grid>
              </Grid>
              {index < papers.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))}

          <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? 'Saving...' : `Create Subject with ${papers.length} Paper(s)`}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SubjectWithPapers;
